import './botSettingsModal.scss';

import { InputField } from 'fields/base/InputField/InputField';
import { SelectField } from 'fields/SelectField';
import { FC, memo } from 'react';

import { IAddPairArgs } from 'api/apiPairs/models';
import { useTypedSelector } from 'store';
import { EDexBot, IDexBotSettings } from 'types/bots';
import { IDexPair } from 'types/pairs';
import { Button } from 'ui/Button/Button';
import { ButtonLoading } from 'ui/Button/ButtonLoading';
import { ErrorText } from 'ui/ErrorText/ErrorText';
import { EditBuySellBotSettingsForm } from 'ui/forms/EditBuySellBotSettingsForm/EditBuySellBotSettingsForm';
import { Modal } from 'ui/Modal/Modal';
import { Switcher } from 'ui/Switcher/Switcher';

import { useBotSettingsModal } from './useBotSettingsModal/useBotSettingsModal';

interface IBotSettingsModalProps {
  onClose: () => void;
  onOpen: () => void;
  botSettings?: IDexBotSettings;
  onSave: () => void;
  bot: EDexBot;
}

export const BotSettingsModal: FC<IBotSettingsModalProps> = memo(
  ({ onClose, botSettings, onSave, bot }) => {
    const pair = useTypedSelector(store => store.pairs.selectedDexPair);

    const { values, setFieldValue, loading, handleSubmit, formError, edited } = useBotSettingsModal(
      {
        pair: pair as IAddPairArgs & IDexPair,
        botSettings,
        onSave,
        onClose,
        bot,
      },
    );

    return (
      <Modal title="Settings" onClose={onClose} edited={edited}>
        <EditBuySellBotSettingsForm handleSubmit={handleSubmit}>
          <div className="mm-bot-settings-modal">
            <InputField
              label="Slippage (%)"
              type="decimal-number"
              value={values.slippage_percent}
              setValue={v => setFieldValue('slippage_percent', v ? v : '', { triggerEdit: true })}
            />
            <SelectField
              items={['default', 'random']}
              itemToString={v => (v === 'default' ? 'Min available balance' : 'Random')}
              label="Reserve wallets priority"
              selectedItem={values.reserve_wallets_priority}
              onSelectItem={v => (v ? setFieldValue('reserve_wallets_priority', v) : undefined)}
            />
            <div className="grid grid-flow-col justify-between">
              <span className="text-primary font-bold">Private transactions</span>
              <Switcher
                value={values.send_private_transactions}
                onChange={v =>
                  setFieldValue('send_private_transactions', v ? v : false, { triggerEdit: true })
                }
              />
            </div>
            <div className="grid grid-flow-col justify-between">
              <span className="text-primary font-bold">Enable bot</span>
              <Switcher
                value={values.is_enabled}
                onChange={v => setFieldValue('is_enabled', v ? v : false, { triggerEdit: true })}
              />
            </div>
            <div className="mm-bot-settings-modal__actions">
              {formError && <ErrorText>{formError}</ErrorText>}
              {loading ? <ButtonLoading /> : <Button type="submit">Save</Button>}
            </div>
          </div>
        </EditBuySellBotSettingsForm>
      </Modal>
    );
  },
);
